/** Where one filing stands, as a line of steps with its date and the next move.
 *
 * Drawn from the checks read when it has answered (the server's four parts, then the issue step) and
 * from the verdict alone while it has not or when it failed: the spine then has three steps and the
 * line under it says the four parts could not be read, rather than drawing steps it cannot vouch
 * for. **Nothing here is a percentage and nothing is ticked that the server did not say.** The
 * verdict block below stays the one place the F07 ladder is worded in full; this is the same fact
 * as a line you can scan.
 */

import { useFilingChecks } from "../api/filingChecks";
import type { Obligation, Report, Scope2Method } from "../api/queries";
import { FilingSpine } from "../components/FilingSpine";
import { StatusChip } from "../components/ui/StatusChip";
import { shortDate } from "../lib/format";
import { currentStep, stepsFromChecks, stepsFromVerdict } from "./filingSpine";
import styles from "./ReportsJourney.module.css";
import { isoToday } from "./readinessModel";
import { dueOf, standingOf } from "./reportsModel";

export function FilingJourney({
  active,
  report,
  method,
  onGoTab,
}: {
  active: Obligation;
  report: Report;
  method: Scope2Method;
  onGoTab: (tab: string) => void;
}) {
  const checks = useFilingChecks(active.obligation_id, method);
  const standing = standingOf(active, { state: "ready", report });
  const steps = checks.data
    ? stepsFromChecks(checks.data.parts, report.verdict)
    : stepsFromVerdict(report.verdict, standing.blocking);
  const due = dueOf(active, standing.group, isoToday(new Date()));
  const now = currentStep(steps);
  // Where the next move is made. Issuing is never gated on the verdict (ADR-0020), so the way to the
  // Issue tab is always open; this only chooses which tab to suggest.
  const goTo = report.verdict === "approved" || report.verdict === "checks_passed" ? "issue" : "checks";

  return (
    <section className={styles.journey} aria-label="Where this filing stands" data-testid="filing-journey">
      <FilingSpine steps={steps} label="Steps of this filing" testId="filing-spine" />
      <div className={styles.side}>
        <p className={styles.due} data-testid="filing-due-line">
          {active.due_date ? `Due ${shortDate(active.due_date)}` : "No due date set"}
          <StatusChip tone={due.tone} size="sm">
            {due.words}
          </StatusChip>
        </p>
        <p className={styles.next}>
          {standing.next}
          {now ? <span className={styles.now}> Now: {now.label}.</span> : null}
        </p>
        <button type="button" className={styles.go} onClick={() => onGoTab(goTo)}>
          {goTo === "issue" ? "Go to Issue" : "Go to Checks"}
        </button>
        {checks.error && !checks.data ? (
          <p className={styles.fine} role="status" data-testid="journey-parts-unread">
            The four parts of the work could not be read, so only the verdict is shown here.
          </p>
        ) : null}
      </div>
    </section>
  );
}
